import './coapplicantEditEmploymentPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { employedTypeOptions } from '../../../store/applicantStore/constants/employedTypeOptions';
import { employmentStatusOptions } from '../../../store/applicantStore/constants/employmentStatusOptions';
import { HALF_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/halfYear';
import { monthsAtIncomeOptions } from '../../../store/applicantStore/constants/monthsAtIncomeOptions';
import { previousEmploymentTypeOptions } from '../../../store/applicantStore/constants/previousEmploymentTypeOptions';
import { unemployedTypeOptions } from '../../../store/applicantStore/constants/unemployedTypeOptions';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: {
      coapplicantEmployedGrossAnnualIncome,
      coapplicantUnemployedGrossAnnualIncome,
      coapplicantReviewEditPathname,
      coapplicantReviewEditProgress,
    },
    coapplicantCurrentIncomeEmployerName,
    coapplicantCurrentIncomeEmploymentType,
    coapplicantCurrentIncomeJobTitle,
    coapplicantCurrentIncomeMonthsAt,
    coapplicantPreviousIncomeEmployerName,
    coapplicantPreviousIncomeEmploymentType,
    coapplicantPreviousIncomeGrossAnnual,
    coapplicantPreviousIncomeJobTitle,
    isCoapplicantCurrentlyEmployed,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeEmployerName,
    setCoapplicantCurrentIncomeEmploymentType,
    setCoapplicantCurrentIncomeGrossAnnual,
    setCoapplicantCurrentIncomeJobTitle,
    setCoapplicantCurrentIncomeMonthsAt,
    setCoapplicantPreviousIncomeEmployerName,
    setCoapplicantPreviousIncomeEmploymentType,
    setCoapplicantPreviousIncomeGrossAnnual,
    setCoapplicantPreviousIncomeJobTitle,
    setIsCoapplicantCurrentlyEmployed,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      currentEmployerNameValue: coapplicantCurrentIncomeEmployerName,
      currentIncomeMonthsAtValue: coapplicantCurrentIncomeMonthsAt,
      employmentTypeValue: coapplicantCurrentIncomeEmploymentType,
      grossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantEmployedGrossAnnualIncome,
      ),
      isCurrentlyEmployedValue: isCoapplicantCurrentlyEmployed,
      jobTitleValue: coapplicantCurrentIncomeJobTitle,
      previousEmployerNameValue: coapplicantPreviousIncomeEmployerName,
      previousEmploymentTypeValue: coapplicantPreviousIncomeEmploymentType,
      previousGrossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantPreviousIncomeGrossAnnual,
      ),
      previousJobTitleValue: coapplicantPreviousIncomeJobTitle,
      unemploymentGrossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantUnemployedGrossAnnualIncome,
      ),
      unemploymentTypeValue: coapplicantCurrentIncomeEmploymentType,
    },
    delayError: 200,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const shouldShowEmployedTypeFields =
    watch('isCurrentlyEmployedValue') === YES;

  const shouldShowPreviousEmployedTypeFields = employedTypes.includes(
    watch('previousEmploymentTypeValue') as EmploymentType,
  );

  const shouldShowPreviousEmploymentSection =
    watch('currentIncomeMonthsAtValue') === HALF_YEAR_IN_MONTHS_AS_STRING;

  const employmentStatusValidation = {
    required: t('validation.employmentStatus.required'),
  };

  const employmentTypeValidation = {
    required: t('validation.employmentType.required'),
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const monthsAtIncomeValidation = {
    required: t('validation.employmentDuration.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentEmployerNameValue,
      currentIncomeMonthsAtValue,
      employmentTypeValue,
      grossAnnualIncomeValue,
      isCurrentlyEmployedValue,
      jobTitleValue,
      previousEmployerNameValue,
      previousEmploymentTypeValue,
      previousGrossAnnualIncomeValue,
      previousJobTitleValue,
      unemploymentGrossAnnualIncomeValue,
      unemploymentTypeValue,
    } = values;

    if (
      !isCurrentlyEmployedValue ||
      (shouldShowEmployedTypeFields && !employmentTypeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentTypeValue) ||
      (shouldShowEmployedTypeFields && !currentEmployerNameValue) ||
      (shouldShowEmployedTypeFields && !jobTitleValue) ||
      (shouldShowEmployedTypeFields && !grossAnnualIncomeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentGrossAnnualIncomeValue) ||
      !currentIncomeMonthsAtValue
    ) {
      return;
    }

    if (
      shouldShowPreviousEmploymentSection &&
      (!previousEmploymentTypeValue ||
        (shouldShowPreviousEmployedTypeFields && !previousEmployerNameValue) ||
        (shouldShowPreviousEmployedTypeFields && !previousJobTitleValue) ||
        !previousGrossAnnualIncomeValue)
    ) {
      return;
    }

    setCoapplicantCurrentIncomeMonthsAt(currentIncomeMonthsAtValue);
    setIsCoapplicantCurrentlyEmployed(isCurrentlyEmployedValue);

    if (shouldShowEmployedTypeFields) {
      setCoapplicantCurrentIncomeEmployerName(currentEmployerNameValue);
      setCoapplicantCurrentIncomeEmploymentType(employmentTypeValue);
      setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);
      setCoapplicantCurrentIncomeJobTitle(jobTitleValue);
    } else {
      setCoapplicantCurrentIncomeEmployerName(null);
      setCoapplicantCurrentIncomeEmploymentType(unemploymentTypeValue);
      setCoapplicantCurrentIncomeGrossAnnual(
        unemploymentGrossAnnualIncomeValue,
      );
      setCoapplicantCurrentIncomeJobTitle(null);
    }

    if (shouldShowPreviousEmploymentSection) {
      setCoapplicantPreviousIncomeEmploymentType(previousEmploymentTypeValue);
      setCoapplicantPreviousIncomeGrossAnnual(previousGrossAnnualIncomeValue);
    } else {
      setCoapplicantPreviousIncomeEmploymentType(null);
      setCoapplicantPreviousIncomeGrossAnnual(null);
    }

    if (
      shouldShowPreviousEmploymentSection &&
      shouldShowPreviousEmployedTypeFields
    ) {
      setCoapplicantPreviousIncomeEmployerName(previousEmployerNameValue);
      setCoapplicantPreviousIncomeJobTitle(previousJobTitleValue);
    } else {
      setCoapplicantPreviousIncomeEmployerName(null);
      setCoapplicantPreviousIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-employment-page" sx={theme.pageLayout}>
        <Header
          progress={coapplicantReviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEditEmployment.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditEmployment.form.h1}>
                {t('CoapplicantEditEmploymentPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditEmployment.form.h2}>
                {t('CoapplicantEditEmploymentPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditEmployment.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentStatusSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentStatusSelect
                    }
                    label={t('CoapplicantEditEmploymentPage.employmentStatus')}
                    name="isCurrentlyEmployedValue"
                    options={employmentStatusOptions}
                    rules={employmentStatusValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .employmentTypeSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .employmentTypeSelect
                        }
                        label={t('CoapplicantEditEmploymentPage.type')}
                        name="employmentTypeValue"
                        options={employedTypeOptions}
                        rules={employmentTypeValidation}
                      />
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .unemploymentTypeSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .unemploymentTypeSelect
                        }
                        label={t('CoapplicantEditEmploymentPage.type')}
                        name="unemploymentTypeValue"
                        options={unemployedTypeOptions}
                        rules={employmentTypeValidation}
                      />
                    </div>
                  )}
                </div>

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'currentEmployerNameValue',
                        employerNameValidation,
                      )}
                      error={!isEmpty(errors.currentEmployerNameValue)}
                      label={t('CoapplicantEditEmploymentPage.employerName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .employerNameInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.currentEmployerNameValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .employerNameInputError
                        }
                      >
                        {toString(errors.currentEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('jobTitleValue', jobTitleValidation)}
                      error={!isEmpty(errors.jobTitleValue)}
                      label={t('CoapplicantEditEmploymentPage.jobTitle')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .jobTitleInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.jobTitleValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .jobTitleInputError
                        }
                      >
                        {toString(errors.jobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <TextField
                        {...register(
                          'grossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        error={!isEmpty(errors.grossAnnualIncomeValue)}
                        label={t(
                          'CoapplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantEditEmployment.form
                                .grossAnnualIncomeInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'grossAnnualIncomeValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'grossAnnualIncomeValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'grossAnnualIncomeValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.grossAnnualIncomeValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .grossAnnualIncomeInputError
                          }
                        >
                          {toString(errors.grossAnnualIncomeValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <TextField
                        {...register(
                          'unemploymentGrossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        error={
                          !isEmpty(errors.unemploymentGrossAnnualIncomeValue)
                        }
                        label={t(
                          'CoapplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantEditEmployment.form
                                .unemploymentGrossAnnualIncomeInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'unemploymentGrossAnnualIncomeValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'unemploymentGrossAnnualIncomeValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'unemploymentGrossAnnualIncomeValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.unemploymentGrossAnnualIncomeValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .unemploymentGrossAnnualIncomeInputError
                          }
                        >
                          {toString(
                            errors.unemploymentGrossAnnualIncomeValue.message,
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentDurationSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentDurationSelect
                    }
                    label={t('CoapplicantEditEmploymentPage.duration')}
                    name="currentIncomeMonthsAtValue"
                    options={monthsAtIncomeOptions}
                    rules={monthsAtIncomeValidation}
                  />
                </div>
              </div>

              {shouldShowPreviousEmploymentSection && (
                <div
                  data-testid={
                    locator.pages.coapplicantEditEmployment.form
                      .previousEmploymentRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form
                        .previousEmploymentH3
                    }
                  >
                    {t('CoapplicantEditEmploymentPage.previousEmployment')}
                  </h3>

                  <div>
                    <Select
                      control={control}
                      errorTestId={
                        locator.pages.coapplicantEditEmployment.form
                          .previousEmploymentTypeSelectError
                      }
                      inputTestId={
                        locator.pages.coapplicantEditEmployment.form
                          .previousEmploymentTypeSelect
                      }
                      label={t('CoapplicantEditEmploymentPage.type')}
                      name="previousEmploymentTypeValue"
                      options={previousEmploymentTypeOptions}
                      rules={employmentTypeValidation}
                    />
                  </div>

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'previousEmployerNameValue',
                          employerNameValidation,
                        )}
                        error={!isEmpty(errors.previousEmployerNameValue)}
                        label={t('CoapplicantEditEmploymentPage.employerName')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantEditEmployment.form
                                .previousEmployerNameInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.previousEmployerNameValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .previousEmployerNameInputError
                          }
                        >
                          {toString(errors.previousEmployerNameValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'previousJobTitleValue',
                          jobTitleValidation,
                        )}
                        error={!isEmpty(errors.previousJobTitleValue)}
                        label={t('CoapplicantEditEmploymentPage.jobTitle')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantEditEmployment.form
                                .previousEmploymentJobTitleInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.previousJobTitleValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .previousEmploymentJobTitleInputError
                          }
                        >
                          {toString(errors.previousJobTitleValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'previousGrossAnnualIncomeValue',
                        grossAnnualIncomeValidation,
                      )}
                      error={!isEmpty(errors.previousGrossAnnualIncomeValue)}
                      label={t(
                        'CoapplicantEditEmploymentPage.grossAnnualIncome',
                      )}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .previousEmploymentGrossAnnualIncomeInput,
                          inputMode: 'numeric',
                        },
                      }}
                      type="text"
                      variant="outlined"
                      fullWidth
                      onBlur={inputEventHandler.currency.blur(
                        'previousGrossAnnualIncomeValue',
                        setValue,
                        trigger,
                      )}
                      onChange={inputEventHandler.currency.change(
                        'previousGrossAnnualIncomeValue',
                        setValue,
                      )}
                      onFocus={inputEventHandler.currency.focus(
                        'previousGrossAnnualIncomeValue',
                        setValue,
                      )}
                      onKeyDown={inputEventHandler.number.keydown}
                      onPasteCapture={inputEventHandler.number.pasteCapture}
                    />

                    {!isEmpty(errors.previousGrossAnnualIncomeValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmploymentGrossAnnualIncomeInputError
                        }
                      >
                        {toString(
                          errors.previousGrossAnnualIncomeValue.message,
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditEmploymentPage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingCoapplicant}
                    fullWidth
                    submit
                  >
                    {t('CoapplicantEditEmploymentPage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditEmployment.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditEmploymentPage';
